import { createContext, useContext, useEffect, useState } from "react";
import { Organization, OrganizationPublic, OrganizationType } from "../models/shared/Organization";
import { useUser } from "./UserContext";
import { doc, onSnapshot, orderBy, setDoc } from "firebase/firestore";
import { COLLECTIONS, PLAN_DEAL_LIMITS, PlanId } from "../utils/shared/constants";
import { useFirestore } from "reactfire";
import { fromFirebaseDoc } from "../utils/shared/firebase";
import { User } from "../models/shared/User";
import LoadingPage from "../pages/LoadingPage";
import { UserInvitation, UserRole } from "../models/shared/UserInvitation";
import { useFirebaseQuery } from "../hooks/firebaseHooks";

interface OrganizationContextValue {
    organization: Organization;
    orgId: string;
    orgType: OrganizationType;
    orgRole: UserRole;
    allOrganizationIds: string[];
    members: UserInvitation[];
    currentPlanId: PlanId;
    currentPlanDealLimit: number;
}

interface OrganizationContext extends OrganizationContextValue {
    updateOrganization: (update: Partial<Organization>) => Promise<void>;
    switchOrganization: (orgId: string) => Promise<void>;
}

const emptyContextValue: OrganizationContextValue = {
    organization: {
        ownedBy: '',
        createdAt: new Date(),
        dealSteps: [],
    },
    orgId: '',
    orgType: OrganizationType.arbTask,
    orgRole: UserRole.WORKER,
    allOrganizationIds: [],
    members: [],
    currentPlanId: 'free',
    currentPlanDealLimit: 0,
};

const organizationContext = createContext<OrganizationContext>({
    ...emptyContextValue,
    updateOrganization: async () => { },
    switchOrganization: async () => { },
});

export const useOrganization = () => useContext(organizationContext);

interface ContextProviderProps {
    children: React.ReactNode;
}

export const OrganizationContextProvider = (props: ContextProviderProps) => {
    const [currentOrgId, setCurrentOrgId] = useState<string | null>(null);
    const [allOrganizationIds, setAllOrganizationIds] = useState<string[]>([]);
    const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
    const userContext = useUser();
    const firestore = useFirestore();

    const [members, setMembers] = useState<UserInvitation[]>([]);
    const [userInvitations] = useFirebaseQuery<UserInvitation>(
        `${COLLECTIONS.ORGANIZATIONS}/${currentOrgId}/${COLLECTIONS.USER_INVITATIONS}`,
        orderBy('createdAt', 'desc'),
    );

    useEffect(() => {
        const currentUserEmail = userContext?.user?.email || '';
        const ownerEmail = currentOrganization?.email || '';
        const existingEmails = new Set(userInvitations.map(inv => inv.email));

        const missingOwnerEmails = Array.from(new Set(
            [ownerEmail, currentUserEmail].filter(email => email && !existingEmails.has(email))
        ));
        const syntheticMembers: UserInvitation[] = missingOwnerEmails.map(email => ({
            email,
            role: UserRole.ADMIN,
            status: 'accepted',
            createdAt: new Date(),
        }));

        const membersList = [...syntheticMembers, ...userInvitations];
        const sortedMembers = membersList.sort((a, b) => (a.email || '').localeCompare(b.email || ''));
        setMembers(sortedMembers);
    }, [userInvitations, userContext?.user?.email, currentOrganization?.email])

    useEffect(() => {
        if (!userContext.loaded) return;

        const organizations = [...(userContext.claims.ownerOf || [])]
            .concat(userContext.claims.userOf || [])
            .concat(userContext.claims.userWorkerOf || []);

        setCurrentOrgId(
            userContext.user?.settings?.selectedOrganization || organizations[0],
        );
        setAllOrganizationIds(organizations);
    }, [userContext.loaded, userContext.claims, userContext.user?.settings]);

    useEffect(() => {
        if (!currentOrgId) return;
        return onSnapshot(
            doc(firestore, `${COLLECTIONS.ORGANIZATIONS}/${currentOrgId}`),
            (docSnap) => {
                const org = fromFirebaseDoc<Organization>(docSnap);
                if (!org) {
                    // The organization no longer exists (e.g. it was just deleted while
                    // selected) — fall back to another organization the user belongs to,
                    // or force a fresh load so the normal onboarding flow can provision a
                    // new one. Without this, currentOrganization would stay null forever
                    // and the app would be stuck on the loading screen.
                    const fallbackOrgId = allOrganizationIds.find(id => id !== currentOrgId);
                    if (fallbackOrgId) {
                        switchOrganization(fallbackOrgId);
                    } else {
                        window.location.href = '/';
                    }
                    return;
                }
                setCurrentOrganization(org);
            },
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentOrgId, firestore]);

    const updateOrganization = async (update: Partial<Organization>) => {
        if (!currentOrgId || !currentOrganization) return;

        setCurrentOrganization({ ...currentOrganization, ...update });
        const orgRef = doc(firestore, `${COLLECTIONS.ORGANIZATIONS}/${currentOrgId}`);
        await setDoc(orgRef, update, { merge: true });
    };

    const switchOrganization = async (orgId: string) => {
        if (orgId === currentOrgId || !allOrganizationIds.includes(orgId)) return;

        const userUpdate: Partial<User> = {
            settings: {
                ...(userContext.user?.settings || {}),
                selectedOrganization: orgId,
            },
        };

        setCurrentOrgId(orgId);
        setCurrentOrganization(null);
        userContext.updateUser(userUpdate);
    };

    if (!currentOrgId || !currentOrganization) {
        return <LoadingPage />;
    }

    let orgRole = UserRole.WORKER;
    if (userContext.claims.ownerOf.includes(currentOrgId)) {
        orgRole = UserRole.ADMIN;
    } else if (userContext.claims.userOf.includes(currentOrgId)) {
        orgRole = UserRole.MEMBER;
    }
    const currentPlanId: PlanId = currentOrganization.billing?.planId || 'free';
    const isOnTrial = !!currentOrganization.trialEndsAt && new Date(currentOrganization.trialEndsAt) > new Date();
    const currentPlanDealLimit = isOnTrial ? -1 : PLAN_DEAL_LIMITS[currentPlanId];

    console.log('Rerendering org');
    return (
        <organizationContext.Provider value={{
            organization: currentOrganization,
            orgId: currentOrgId,
            allOrganizationIds: allOrganizationIds,
            orgType: currentOrganization.type || OrganizationType.arbTask,
            orgRole: orgRole,
            switchOrganization,
            updateOrganization,
            members,
            currentPlanId,
            currentPlanDealLimit,
        }}>
            {props.children}
        </organizationContext.Provider>
    );
};

interface UnAuthContextProviderProps {
    orgId: string;
    children: React.ReactNode;
    organization: OrganizationPublic;
}

export const UnAuthOrganizationContextProvider = (props: UnAuthContextProviderProps) => {
    const organization: Organization = {
        ...props.organization,
        ownedBy: '',
        createdAt: new Date(),
    };

    return <organizationContext.Provider value={{
        allOrganizationIds: [props.orgId],
        orgRole: UserRole.MEMBER,
        organization,
        orgId: props.orgId,
        orgType: OrganizationType.arbTask,
        members: [],
        updateOrganization: async () => { },
        switchOrganization: async () => { },
        currentPlanId: 'free',
        currentPlanDealLimit: PLAN_DEAL_LIMITS['free'],
    }} >
        {props.children}
    </organizationContext.Provider>
} 
